<template>
  <div class="detailed-gift-item">
    <!-- ギフト情報 -->
    <div class="detailed-gift-header">
      <div class="detailed-gift-icon">
          <img 
            :src="giftIcon" 
            :alt="giftName" 
            class="detailed-icon-img"
          />
      </div>
      <div class="detailed-gift-info">
        <div class="detailed-gift-count">{{ $t('commonGiftAmount', { count: giftCount }) }}</div>
        <div class="detailed-gift-name">{{ giftName }}</div>
      </div>
    </div>

    <!-- 贈ってくれた視聴者 / 贈った配信者 -->
    <div v-if="hasViewers" class="gift-viewers">
      <div class="viewers-label">{{ defaultRecipientLabel }}</div>
      <div class="viewers-list">
        <div 
          v-for="(viewer, vIndex) in userList" 
          :key="vIndex" 
          class="viewer-item"
        >
          <div class="viewer-avatar">
            <img 
              :src="viewer.avatar" 
              :alt="viewer.name" 
              class="viewer-avatar-img"
            />
          </div>
          <div class="viewer-info">
            <div class="viewer-count">{{ $t('commonGiftAmount', { count: viewer.count }) }}</div>
            <div class="viewer-name">{{ viewer.name }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Vue, Component, Prop } from 'vue-property-decorator'
import { HistoryAlbumReceiveGiftRecord } from '@/api/pb/HistoryAlbum/Common/HistoryAlbumReceiveGiftRecord'
import { HistoryAlbumSendGiftRecord } from '@/api/pb/HistoryAlbum/Common/HistoryAlbumSendGiftRecord'

@Component({})
export default class DetailedGift extends Vue {
  @Prop({ type: Object, required: true })
  gift!: HistoryAlbumReceiveGiftRecord | HistoryAlbumSendGiftRecord

  @Prop({ type: String, default: '' })
  recipientLabel!: string

  // デフォルトのラベルを取得
  get defaultRecipientLabel(): string {
    return this.recipientLabel || (this.$t('historiesTopReceiveGiftFrom').toString())
  }

  // 視聴者がいるかどうかを判定
  get hasViewers(): boolean {
    if (this.isReceiveGiftRecord) {
      const receiveGift = this.gift as HistoryAlbumReceiveGiftRecord
      return !!(receiveGift.topReceiveUserList && receiveGift.topReceiveUserList.length > 0)
    } else {
      const sendGift = this.gift as HistoryAlbumSendGiftRecord
      return !!(sendGift.topSendUserList && sendGift.topSendUserList.length > 0)
    }
  }

  // 受信ギフトレコードかどうかを判定
  get isReceiveGiftRecord(): boolean {
    return 'topReceiveUserList' in this.gift
  }

  // ギフトアイコンURL
  get giftIcon(): string {
    return this.gift.gift?.iconUrl || ''
  }

  // ギフト名
  get giftName(): string {
    return this.gift.gift?.name || ''
  }

  // ギフト総数（フォーマット済み）
  get giftCount(): string {
    return this.gift.totalCount.toLocaleString()
  }

  // ユーザーリスト
  get userList() {
    if (this.isReceiveGiftRecord) {
      const receiveGift = this.gift as HistoryAlbumReceiveGiftRecord
      return receiveGift.topReceiveUserList.map(userRecord => ({
        avatar: userRecord.user?.iconUrl || '',
        count: userRecord.count.toLocaleString(),
        name: userRecord.user?.nickname || ''
      }))
    } else {
      const sendGift = this.gift as HistoryAlbumSendGiftRecord
      return sendGift.topSendUserList.map(userRecord => ({
        avatar: userRecord.user?.iconUrl || '',
        count: userRecord.count.toLocaleString(),
        name: userRecord.user?.nickname || ''
      }))
    }
  }
}
</script>

<style scoped>
.detailed-gift-item {
  position: relative;
  border-radius: 4px;
  flex-shrink: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 0;
}

.detailed-gift-header {
  position: relative;
  flex-shrink: 0;
  width: 100%;
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
  justify-content: flex-start;
  padding: 0;
}

.detailed-gift-icon {
  background: #ffffff;
  border: 1px solid #c5d1ff;
  position: relative;
  border-radius: 4px;
  flex-shrink: 0;
  width: 70px;
  height: 70px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.detailed-icon-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.detailed-gift-info {
  flex: 1;
  min-height: 1px;
  min-width: 1px;
  position: relative;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
  justify-content: flex-start;
  line-height: 0;
  font-style: normal;
  padding: 0;
  color: #ffffff;
  text-align: left;
}

.detailed-gift-count {
  font-weight: 400;
  position: relative;
  flex-shrink: 0;
  font-size: 16px;
  width: 100%;
  line-height: normal;
}

.detailed-gift-name {
  font-weight: 600;
  position: relative;
  flex-shrink: 0;
  font-size: 20px;
  width: 100%;
  line-height: 24px;
}

/* 視聴者情報 */
.gift-viewers {
  position: relative;
  flex-shrink: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 0;
}

.viewers-label {
  font-weight: 600;
  line-height: 0;
  font-style: normal;
  position: relative;
  flex-shrink: 0;
  color: #ffffff;
  font-size: 12px;
  text-align: left;
  width: 100%;
  line-height: 1;
}

.viewers-list {
  position: relative;
  flex-shrink: 0;
  width: 100%;
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 0;
}

.viewer-item {
  flex: 1;
  min-height: 1px;
  min-width: 1px;
  position: relative;
  flex-shrink: 0;
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: center;
  justify-content: flex-start;
  padding: 0;
}

.viewer-avatar {
  border-radius: 99px;
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.viewer-avatar-img {
  border-radius: 99px;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.viewer-info {
  flex: 1;
  min-height: 1px;
  min-width: 1px;
  position: relative;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  line-height: 0;
  font-style: normal;
  padding: 0;
  color: #ffffff;
  text-align: left;
}

.viewer-count {
  display: flex;
  flex-direction: column;
  font-weight: 300;
  justify-content: center;
  position: relative;
  flex-shrink: 0;
  font-size: 12px;
  width: 100%;
  line-height: normal;
}

.viewer-name {
  font-weight: 600;
  position: relative;
  flex-shrink: 0;
  font-size: 14px;
  width: 100%;
  line-height: normal;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
  word-break: break-all;
}
</style>
